import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMenu } from '../contexts/MenuContext';
import { MenuItem } from '../types';

interface MenuCategoryProps {
  item: MenuItem;
}

const MenuCategory: React.FC<MenuCategoryProps> = ({ item }) => {
  const { addToCart } = useMenu();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Initialize showDetails based on URL parameter immediately
  const itemIdFromUrl = searchParams.get('item');
  const [quantity, setQuantity] = useState(1);
  const [selectedExtras, setSelectedExtras] = useState<{ extra: any; quantity: number }[]>([]);
  const [notes, setNotes] = useState('');
  const [showDetails, setShowDetails] = useState(itemIdFromUrl === item.id);
  const [showImageModal, setShowImageModal] = useState(false);

  // Check if this item should be shown in modal based on URL
  useEffect(() => {
    const itemId = searchParams.get('item');
    if (itemId === item.id && !showDetails) {
      setShowDetails(true);
    } else if (itemId !== item.id && showDetails) {
      setShowDetails(false);
    }
  }, [searchParams, item.id, showDetails]);

  const handleOpenModal = () => {
    navigate(`?item=${item.id}`, { replace: false });
  };

  const handleCloseModal = () => {
    navigate(-1);
  };

  const handleAddToCart = () => {
    if (!item.isAvailable) {
      return; // Don't allow adding unavailable items to cart
    }
    addToCart(item, quantity, selectedExtras, notes);
    setQuantity(1);
    setSelectedExtras([]);
    setNotes('');
    // Navigate to cart instead of closing modal
    navigate('?cart=true', { replace: false });
  };

  const handleExtraChange = (extra: any, extraQuantity: number) => {
    if (extraQuantity <= 0) {
      setSelectedExtras(selectedExtras.filter(e => e.extra.id !== extra.id));
    } else {
      const existingExtra = selectedExtras.find(e => e.extra.id === extra.id);
      if (existingExtra) {
        setSelectedExtras(selectedExtras.map(e => 
          e.extra.id === extra.id ? { ...e, quantity: extraQuantity } : e
        ));
      } else {
        setSelectedExtras([...selectedExtras, { extra, quantity: extraQuantity }]);
      }
    }
  };

  const getExtraQuantity = (extraId: string) => {
    const extra = selectedExtras.find(e => e.extra.id === extraId);
    return extra ? extra.quantity : 0;
  };

  const getTotalPrice = () => {
    const itemTotal = item.price * quantity;
    const extrasTotal = selectedExtras.reduce((total, extra) => 
      total + (extra.extra.price * extra.quantity), 0
    );
    return itemTotal + extrasTotal;
  };

  return (
    <>
    {/* Render card only when not showing in modal from URL */}
    {itemIdFromUrl !== item.id && (
    <div 
      className={`bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-lg transition-all cursor-pointer relative ${
        !item.isAvailable ? 'opacity-60 blur-sm pointer-events-none' : ''
      }`}
      onClick={() => item.isAvailable && handleOpenModal()}
    >
      {/* Unavailable Overlay */}
      {!item.isAvailable && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
          <div className="text-center text-white">
            <div className="text-lg font-bold mb-1">Unavailable</div>
            <div className="text-xs">This item is currently not available</div>
          </div>
        </div>
      )}
      
      {/* Image - Portrait orientation */}
      {item.imageUrl && (
        <div 
          className="aspect-[3/4] bg-gray-200 relative overflow-hidden group"
        >
          <img
            src={item.imageUrl}
            alt={item.name}
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          {/* Price Badge */}
          <div className="absolute bottom-2 right-2 z-10">
            <span className="text-white px-2.5 py-1 text-sm font-bold bg-black bg-opacity-40 rounded-md backdrop-blur-sm" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
              £{item.price.toFixed(2)}
            </span>
          </div>
        </div>
      )}

      <div className="p-2 sm:p-3">
        {/* Item Name */}
        <h3 className="text-sm sm:text-base font-extrabold line-clamp-2 text-transparent bg-clip-text bg-gradient-to-r from-red-700 via-red-600 to-red-700 text-center">
          {item.name}
        </h3>
        {!item.imageUrl && (
          <div className="text-center mt-1">
            <span className="text-sm font-bold bg-gray-600 text-white px-2 py-0.5 rounded shadow">
              £{item.price.toFixed(2)}
            </span>
          </div>
        )}
      </div>

      {/* Image Lightbox Modal */}
      {showImageModal && item.imageUrl && (
        <div 
          className="fixed inset-0 bg-white/95 backdrop-blur-md flex items-center justify-center z-50 p-4"
          onClick={() => setShowImageModal(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative max-w-5xl w-full bg-white rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute top-4 right-4 z-20 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors"
            >
              <span className="text-2xl leading-none">×</span>
            </button>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              {/* Image Section */}
              <div className="bg-gray-100 flex items-center justify-center p-8 md:p-12">
                <div className="w-full aspect-square bg-white rounded-lg overflow-hidden shadow-lg">
                  <img
                    src={item.imageUrl}
                    alt={item.name}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Details Section */}
              <div className="p-6 md:p-8 flex flex-col">
                <div className="flex-1">
                  <h3 className="text-2xl md:text-3xl font-extrabold mb-3 text-transparent bg-clip-text bg-gradient-to-r from-red-700 via-red-600 to-red-700">{item.name}</h3>
                  <p className="text-gray-600 text-sm md:text-base mb-4">{item.description}</p>
                  
                  <div className="mb-4">
                    <p className="text-3xl font-bold text-gray-900">£{item.price.toFixed(2)}</p>
                  </div>

                  {/* Extras Preview */}
                  {item.extras && item.extras.length > 0 && (
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Available Extras:</h4>
                      <div className="flex flex-wrap gap-2">
                        {item.extras.map((extra) => (
                          <span 
                            key={extra.id}
                            className="text-xs bg-gray-100 text-gray-700 px-3 py-1 rounded-full"
                          >
                            {extra.name} (+£{extra.price.toFixed(2)})
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Quantity Controls */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Quantity:</span>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <span className="w-8 text-center font-semibold text-lg">{quantity}</span>
                      <button
                        onClick={() => setQuantity(quantity + 1)}
                        className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setShowImageModal(false);
                        setShowDetails(true);
                      }}
                      className="flex-1 bg-white border-2 border-gray-600 text-gray-700 hover:bg-gray-50 font-bold rounded text-sm py-3 transition-colors shadow"
                    >
                      Customize Order
                    </button>
                    <button
                      onClick={() => {
                        handleAddToCart();
                        setShowImageModal(false);
                      }}
                      className="flex-1 bg-gray-600 text-white hover:bg-gray-700 font-bold rounded text-sm py-3 transition-colors shadow-lg"
                    >
                      Add to Cart
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
    )}

      {/* Details Modal - Outside card div */}
      {showDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg max-w-4xl w-full max-h-[95vh] overflow-hidden relative"
          >
            {/* Close Button - Top Right Corner */}
                <button
              onClick={handleCloseModal}
              className="absolute top-0 right-0 z-30 w-8 h-8 bg-white hover:bg-gray-100 transition-colors flex items-center justify-center text-gray-600 hover:text-gray-800 rounded-bl border-l border-b border-gray-200"
                >
              <span className="text-xl leading-none">×</span>
                </button>

            <div className="flex flex-col md:flex-row max-h-[95vh]">
              {/* Left Side - Image (Desktop) */}
              {item.imageUrl && (
                <div className="md:w-1/2 bg-gray-100 flex items-center justify-center p-3 md:p-4 lg:p-6">
                  <div className="aspect-square w-full max-w-[250px] md:max-w-[280px] lg:max-w-full bg-gray-200 rounded-lg overflow-hidden">
                    <img
                      src={item.imageUrl}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              )}

              {/* Right Side - Details */}
              <div className="md:w-1/2 flex flex-col overflow-y-auto min-h-0">
                <div className="flex-1 p-3 md:p-4 lg:p-5">
                  
                  <div className="mb-3 md:mb-4">
                    <h3 className="text-xl md:text-2xl lg:text-3xl font-extrabold text-center mb-2 text-transparent bg-clip-text bg-gradient-to-r from-red-700 via-red-600 to-red-700">{item.name}</h3>
                    <p className="text-gray-600 text-xs sm:text-sm text-center mt-2">{item.description}</p>
              </div>

              {/* Extras - collapsible on small screens */}
              {item.extras.length > 0 && (
                <div className="mb-3">
                  <details className="md:open" open>
                    <summary className="text-sm sm:text-base font-medium text-gray-700 mb-2 text-center cursor-pointer select-none">
                      Extras
                    </summary>
                    <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
                      {item.extras.map((extra) => (
                        <div key={extra.id} className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm text-gray-800">{extra.name}</p>
                            <p className="text-xs text-gray-500">+£{extra.price.toFixed(2)}</p>
                          </div>
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => handleExtraChange(extra, getExtraQuantity(extra.id) - 1)}
                              className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
                            >
                              <Minus className="h-3 w-3" />
                            </button>
                            <span className="w-6 text-center font-semibold text-xs">
                              {getExtraQuantity(extra.id)}
                            </span>
                            <button
                              onClick={() => handleExtraChange(extra, getExtraQuantity(extra.id) + 1)}
                              className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </details>
                </div>
              )}

                </div>

                {/* Sticky Bottom Section - Special Instructions, Quantity, Total, Add to Cart */}
                <div className="sticky bottom-0 p-3 md:p-4 lg:p-5 pt-3 border-t border-gray-200 bg-white z-10">
              {/* Notes */}
                  <div className="mb-2 md:mb-3">
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent text-xs sm:text-sm"
                  rows={2}
                      placeholder="Special Instructions (Optional)"
                />
              </div>

                  {/* Quantity */}
                  <div className="mb-2 md:mb-3">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-700">Quantity:</span>
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
                  >
                    <Minus className="h-3 w-3" />
                  </button>
                  <span className="w-6 text-center font-semibold text-sm">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
                  >
                    <Plus className="h-3 w-3" />
                  </button>
                </div>
                </div>

                  {/* Total */}
                  <div className="flex justify-between items-center mb-2 md:mb-3">
                    <span className="text-sm font-medium text-gray-700">Total:</span>
                    <span className="text-xl font-bold text-gray-900">£{getTotalPrice().toFixed(2)}</span>
              </div>

              {/* Add to Cart Button */}
              <button
                onClick={handleAddToCart}
                    className="w-full bg-gray-600 text-white hover:bg-gray-700 font-bold rounded text-sm py-2 md:py-3 transition-colors shadow-lg"
              >
                Add to Cart - £{getTotalPrice().toFixed(2)}
              </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default MenuCategory;

